import { clipboardImageFile } from './tableImport';

/**
 * Browser-clipboard extraction for the block Paste button. Two entry points:
 *
 * - `readClipboardPayload()` — the async path (`navigator.clipboard.read()`),
 *   used by the Paste button on click. Works in Chromium (Windows/Mac) for
 *   `text/html` + `image/*`; throws `ClipboardUnavailableError` when the API is
 *   missing, the context is insecure, or permission is denied (Firefox).
 * - `payloadFromDataTransfer()` — the sync path, used by the one-shot
 *   `paste`-event fallback and by drag-and-drop.
 *
 * Both return the same normalised `{ html, text, image }`; `parsePastedContent`
 * decides what to do with it.
 */

export interface ClipboardPayload {
  html: string;
  text: string;
  image: File | null;
}

export class ClipboardUnavailableError extends Error {
  constructor(message = 'Clipboard read is not available in this browser.') {
    super(message);
    this.name = 'ClipboardUnavailableError';
  }
}

const EMPTY: ClipboardPayload = { html: '', text: '', image: null };

export function payloadFromDataTransfer(data: DataTransfer | null): ClipboardPayload {
  if (!data) return { ...EMPTY };
  return {
    html: data.getData('text/html') ?? '',
    text: data.getData('text/plain') ?? '',
    image: clipboardImageFile(data),
  };
}

function canReadClipboard(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.read === 'function' &&
    (typeof window === 'undefined' || window.isSecureContext !== false)
  );
}

async function imageFromItem(item: ClipboardItem, type: string): Promise<File> {
  const blob = await item.getType(type);
  const ext = type.split('/')[1] || 'png';
  return new File([blob], `pasted-image.${ext}`, { type: blob.type || type });
}

async function absorbType(
  payload: ClipboardPayload,
  item: ClipboardItem,
  type: string,
): Promise<void> {
  if (type === 'text/html' && !payload.html) {
    payload.html = await (await item.getType(type)).text();
  } else if (type === 'text/plain' && !payload.text) {
    payload.text = await (await item.getType(type)).text();
  } else if (type.startsWith('image/') && !payload.image) {
    payload.image = await imageFromItem(item, type);
  }
}

/**
 * Chrome sanitises `text/html` from `navigator.clipboard.read()` — it drops the
 * `<style>` block, which is where Excel/Sheets keep class-based cell formatting
 * (bold, background). The `unsanitized` option (Chrome 126+) returns the raw
 * HTML; fall back to the plain read where it isn't supported.
 */
async function readClipboardItems(): Promise<ClipboardItem[]> {
  try {
    const read = navigator.clipboard.read as (opts?: {
      unsanitized?: string[];
    }) => Promise<ClipboardItem[]>;
    return [...(await read({ unsanitized: ['text/html'] }))];
  } catch {
    return [...(await navigator.clipboard.read())];
  }
}

export async function readClipboardPayload(): Promise<ClipboardPayload> {
  if (!canReadClipboard()) throw new ClipboardUnavailableError();

  let items: ClipboardItem[];
  try {
    items = await readClipboardItems();
  } catch (err) {
    throw new ClipboardUnavailableError((err as Error)?.message);
  }

  const payload: ClipboardPayload = { html: '', text: '', image: null };
  for (const item of items) {
    for (const type of item.types) {
      await absorbType(payload, item, type);
    }
  }
  return payload;
}
